import { CalendarContainer, CalendarLoadingContainer, MessageContainer } from './calendar-page-calendar.style'
import { CalendarPageCell } from '../calendar-page-cell/calendar-page-cell'
import { CalendarProvider }  from 'old/context/calendar-provider'
import { useCalendarSlots, useConfig } from 'old/api'
import { useEffect, useMemo } from 'react'
import { Calendar } from 'old/components/calendar/calendar/calendar'
import { Loading, NotFound, PageError, WhiteShadowLoading } from 'old/components/styled'
import { calcTimezoneOffset, getIsoStringYearMonthDay, useTranslate, useWindowDimensions }  from 'old/utils'
import { CalendarSlot, CalendarSlotTeamMember, TeamMember }  from 'old/types'
import { CALENDAR_PAGE_CALENDAR_SHADOW_Z_INDEX } from 'z-indexes'

type CalendarPageCellProps = {
    date: Date
    selectedTeamMembers: TeamMember[] | undefined
    scale: number
    setAreSlotsLoading: (areSlotsLoading: boolean) => void
}

export const CalendarPageCalendar = ({ date, selectedTeamMembers, scale, setAreSlotsLoading }: CalendarPageCellProps) => {

    //hooks:
    const { SM } = useWindowDimensions()
    const { __ } = useTranslate()
    const { data: config } = useConfig()

    //slots:
    const { data, isLoading, isFetching, isPreviousData } = useCalendarSlots(
        selectedTeamMembers?.map(m => m.id) || [],
        date,
        (selectedTeamMembers?.length ?? 0) > 0
    )

    //data model:
    const dataModel = useMemo(() => {
        
        if(data){

            const teamMembers: CalendarSlotTeamMember[] | undefined = selectedTeamMembers?.map(selectedMember => {

                // find the slots:
                let slots: CalendarSlot[] = []
                data.teamMembers.map(mem => {
                    if(mem.id === selectedMember.id){
                        slots = mem.slots
                    }
                })

                // make new object:
                return {
                    id: selectedMember.id,
                    name: selectedMember.name,
                    image: selectedMember.image,
                    description: selectedMember.description,
                    slots
                }

            })

            return [[{
                date,
                info: {
                    selectedTeamMembers,
                    teamMembers,
                    now: data.now,
                    timezoneOffset: calcTimezoneOffset(data.now),
                    scale
                }
            }]]

        }else{
            return getDefaultDataModel(date, scale)
        }

    }, [data, isLoading, scale])

    // update slots loading:
    useEffect(() => setAreSlotsLoading(isFetching), [isFetching])

    return (!config ? null : (
        <div style={{
            width: '100%',
            height: '100%',
            position: 'relative'
        }}>

            <CalendarLoadingContainer style={{
                overflowX: SM ? 'auto' : 'visible',
                overflowY: SM ? 'hidden' : 'visible'
            }}>

                {/* calendar */}
                <CalendarContainer>
                    <CalendarProvider dataModel={dataModel}>
                        <Calendar cellRenderer={date => <CalendarPageCell date={date} config={config}/>}/>
                    </CalendarProvider>
                </CalendarContainer>

            </CalendarLoadingContainer>

            {/* shadow */}
            {(!data || isPreviousData) && (
                <>
                    <WhiteShadowLoading
                        hasLoading={false}
                        style={{ zIndex: CALENDAR_PAGE_CALENDAR_SHADOW_Z_INDEX }}
                    >

                        <MessageContainer>
                            {
                                selectedTeamMembers && selectedTeamMembers.length === 0 ? (
                                    <NotFound message={__`select_a_team_member`}/>
                                ) : data === null ? (
                                    <PageError/>
                                ) : (
                                    <Loading style={{ width: 'auto', height: 'auto' }}/>
                                )
                            }
                        </MessageContainer>
                        
                    </WhiteShadowLoading>
                </>
            )}

        </div>
    ))
    
}

//getDefaultDataModel:
const getDefaultDataModel = (date: Date, scale: number) => [[{
    date,
    info: {
        selectedTeamMembers: [],
        teamMembers: [],
        now: `${getIsoStringYearMonthDay(new Date())} 09:00`,
        timezoneOffset: 0,
        scale
    }
}]]
